/**
 * FlightHub 2 관제 진입 접근 제어 정의.
 *
 * 클라이언트(GNB 버튼 노출)와 서버(entry API 인가)가 이 모듈 하나를 공유한다 —
 * 두 판정이 갈라지면 버튼은 보이는데 진입이 거부되는(또는 역) 상태가 생긴다.
 * 서버 검증이 유일한 인가 지점이고 클라이언트 판정은 UX 용이다.
 */

/**
 * FlightHub 2 진입이 허용된 앱 사용자의 이메일 도메인.
 *
 * 이 도메인으로 로그인한 사용자에게만 GNB 버튼이 노출되고, 진입 API 인가를
 * 통과한다. 이는 "누가 진입할 수 있는가"(앱 사용자 게이트)를 정하며, 진입 후
 * 어떤 FlightHub 2 신원으로 SSO 되는지(`resolveFlightHubIdentity`)와는 별개다.
 * (소문자 상태로 유지 — 판정은 대소문자·공백을 무시한다.)
 */
export const FLIGHTHUB_ACCESS_EMAIL_DOMAIN =
  "hi-board.partner.dronesquare.co.kr";

/**
 * 이메일이 FlightHub 2 진입 허용 도메인에 속하는지 판정한다 (대소문자·공백 무시).
 *
 * `@<허용 도메인>` 정확 일치만 허용한다 — 로컬파트가 비었거나(`@…`), 하위
 * 도메인(`x.hi-board.…`)이거나, 접미 위조(`hi-board.…evil.com`)인 이메일은
 * 모두 거부한다.
 */
export function isFlightHubAccessAllowed(
  email: string | undefined | null
): boolean {
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  const atIndex = normalized.lastIndexOf("@");
  // atIndex <= 0 → "@" 없음 또는 로컬파트 부재("@domain")
  if (atIndex <= 0) return false;
  const domain = normalized.slice(atIndex + 1);
  return domain === FLIGHTHUB_ACCESS_EMAIL_DOMAIN;
}

/**
 * v1 이 모든 허용 사용자를 SSO 시키는 고정 FlightHub 2 계정 (SAML NameID) 의 기본값.
 *
 * DJI 계정과 바인딩된 FlightHub 2 신원으로, 진입 허용 도메인
 * (`FLIGHTHUB_ACCESS_EMAIL_DOMAIN`)과는 별개의 개념이다.
 *
 * 런타임에는 `FLIGHTHUB_FIXED_ACCOUNT` 환경변수로 오버라이드한다
 * (`resolveFixedFlightHubAccount`). NameID 는 FlightHub 2 에 한 번 바인딩되면
 * 그 값으로 잠겨 다른 DJI 계정으로의 재바인딩이 거부되므로(개발 테스트 등으로
 * 오염되면 콘솔·API 로는 복구 불가), 오염 시 코드 배포 없이 env 만 바꿔 새
 * NameID 로 회전할 수 있도록 값을 env 로 뺀다.
 */
export const FLIGHTHUB_FIXED_ACCOUNT = "testflight@dronesquare.co.kr";

/**
 * SSO 어써션 NameID 로 쓸 고정 FlightHub 2 계정을 런타임 해석한다.
 *
 * `FLIGHTHUB_FIXED_ACCOUNT` env 가 비어있지 않으면 그 값(앞뒤 공백 제거)을, 아니면
 * 기본 상수(`FLIGHTHUB_FIXED_ACCOUNT`)를 쓴다. 서버 전용 — 클라이언트 번들은 이
 * 함수를 호출하지 않는다(고정 계정은 서버에서만 어써션에 실린다). env 인자는
 * 테스트 주입용이며, 프로덕션 호출부는 인자 없이 쓴다.
 */
export function resolveFixedFlightHubAccount(
  env: Record<string, string | undefined> = process.env
): string {
  return env.FLIGHTHUB_FIXED_ACCOUNT?.trim() || FLIGHTHUB_FIXED_ACCOUNT;
}

/**
 * 앱 사용자 이메일로 SAML 어써션 NameID 로 쓸 FlightHub 2 계정을 도출한다.
 *
 * v1 은 모든 허용 사용자가 같은 고정 계정으로 진입한다
 * (`resolveFixedFlightHubAccount` — env 오버라이드 가능). 향후 사용자별 계정
 * 매핑으로 전환할 때 이 함수의 구현만 바꾼다. 호출부는 반드시 실제 앱 사용자
 * 이메일을 넘긴다 — 이메일을 알 수 없는 경로(sso 핸들러 등)는 이 함수가 아니라
 * `resolveFixedFlightHubAccount` 를 직접 써서 "이메일로 신원을 도출한다"는 계약
 * 위반을 피한다.
 */
export function resolveFlightHubIdentity(
  _appUserEmail: string,
  env: Record<string, string | undefined> = process.env
): string {
  return resolveFixedFlightHubAccount(env);
}
